import { Box, Button, Flex, Heading, Input, InputGroup, Text } from '@chakra-ui/react';
import { UseFormReturn, useController } from 'react-hook-form';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { SelectField } from 'src/components/SelectField';
import { DateRangeFilterFields } from '@/components/DateRangeFilterFields';
import { DateRangeChangeHandler } from '@/components/DateRangeFilterFields/types';
import {
  ChallengeInteractivity,
  ChallengeStatus,
  Club,
  translateChallengeInteractivity,
  translateChallengeStatus,
} from '@/model';
import { ChallengeFilters, ChallengeInteractivityFilter, translateChallengeInteractivityFilter } from '../../types';
import { useTranslation } from 'react-i18next';
import TeamFilterSelect from '@/components/TeamFilterSelect';

export interface ChallengeFiltersBoxProps {
  clubs: Club[];
  filtersForm: UseFormReturn<ChallengeFilters>;
  forceRefetch: () => void;
}

export const ChallengeFiltersBox = ({ clubs, filtersForm }: ChallengeFiltersBoxProps) => {
  const { t } = useTranslation();
  const { register, reset, control } = filtersForm;

  const dateValueControl = useController({
    name: 'dateValue',
    control,
  });
  const startDateControl = useController({
    name: 'start_date',
    control,
  });
  const endDateControl = useController({
    name: 'end_date',
    control,
  });

  const handleRangeChange: DateRangeChangeHandler = (startDate, endDate) => {
    startDateControl.field.onChange(startDate);
    endDateControl.field.onChange(endDate);
  };

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>
          {t('pages.application_challenge.components.challenge_filters_box.challenge_filters_box_title')}
        </Heading>
      </Flex>

      <Flex direction={{ base: 'column' }} gap='small' rowGap='micro'>
        <Flex direction={{ base: 'column', md: 'row' }} alignItems={{ md: 'flex-end' }} wrap='wrap' gap={6}>
          <TeamFilterSelect showFlag name='team_uuid' control={filtersForm.control} clubs={clubs} width='20%' />

          <InputGroup w={{ base: '100%', md: 'fit-content' }} display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_challenge.components.challenge_filters_box.challenge_filters_box_challenge_label')}
            </Text>
            <Input variant='flushed' {...useDebouncedFieldRegistry(() => register('title'))} />
          </InputGroup>

          <InputGroup w={{ base: '100%', md: '130px' }} display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_challenge.components.challenge_filters_box.challenge_filters_box_status_label')}
            </Text>
            <SelectField {...register('status')}>
              <option value=''>
                {t('pages.application_challenge.components.challenge_filters_box.challenge_filters_box_status_all_option')}
              </option>
              <option value={ChallengeStatus.DRAFT}>{translateChallengeStatus(ChallengeStatus.DRAFT)}</option>
              <option value={ChallengeStatus.NOT_PUBLISHED}>{translateChallengeStatus(ChallengeStatus.NOT_PUBLISHED)}</option>
              <option value={ChallengeStatus.PUBLISHED}>{translateChallengeStatus(ChallengeStatus.PUBLISHED)}</option>
            </SelectField>
          </InputGroup>

          <InputGroup w={{ base: '100%', md: '130px' }} display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_challenge.components.challenge_filters_box.challenge_filters_box_interactivity_label')}
            </Text>
            <SelectField {...register('interactivity')}>
              <option value=''>
                {t('pages.application_challenge.components.challenge_filters_box.challenge_filters_box_interactivity_all_option')}
              </option>
              <option value={ChallengeInteractivityFilter.NOT_STARTED}>
                {translateChallengeInteractivityFilter(ChallengeInteractivityFilter.NOT_STARTED)}
              </option>
              <option value={ChallengeInteractivity.CLOSED}>
                {translateChallengeInteractivity(ChallengeInteractivity.CLOSED)}
              </option>
              <option value={ChallengeInteractivity.ENABLED}>
                {translateChallengeInteractivity(ChallengeInteractivity.ENABLED)}
              </option>
            </SelectField>
          </InputGroup>

          <DateRangeFilterFields
            onRangeChange={handleRangeChange}
            startDate={startDateControl.field.value}
            endDate={endDateControl.field.value}
            dateValue={dateValueControl.field.value}
            onDateValueChange={dateValueControl.field.onChange}
            inputName='interactive-area-Quizzs-date'
            startDateRootProps={{ w: { base: '100%', md: 'fit-content' } }}
            endDateRootProps={{ w: { base: '100%', md: 'fit-content' } }}
            containerProps={{ flex: 1 }}
          />
        </Flex>

        <Button alignSelf={{ lg: 'end' }} colorScheme='text' variant='outline' mt='10px' onClick={() => reset()}>
          {t('pages.application_challenge.components.challenge_filters_box.challenge_filters_box_clear_filters_button')}
        </Button>
      </Flex>
    </Box>
  );
};
